import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { movies, movieCategories } from '@/mocks/movies';

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const currentCategory = movieCategories.find((c) => c.key === type);
  const categoryName = currentCategory?.name || '全部';
  const [activeType, setActiveType] = useState(type || 'movie');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  useEffect(() => {
    if (type) {
      setActiveType(type);
    }
  }, [type]);

  const filteredMovies = movies
    .filter((m) => m.category === activeType)
    .sort((a, b) => {
      if (sortBy === 'hot') return b.views - a.views;
      if (sortBy === 'new') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
      return b.rating - a.rating;
    });

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${activeType}` },
  ];

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共找到 <strong className="text-accent-400">{filteredMovies.length}</strong> 部{categoryName}资源
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-2 flex-wrap">
                {movieCategories.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.key}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeType === cat.key
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                {[
                  { key: 'hot', label: '最热', icon: 'ri-fire-line' },
                  { key: 'new', label: '最新', icon: 'ri-timer-flash-line' },
                  { key: 'rating', label: '评分', icon: 'ri-star-line' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key as typeof sortBy)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === s.key
                        ? 'bg-background-50 text-foreground-100'
                        : 'text-foreground-500 hover:text-foreground-300'
                    }`}
                  >
                    <i className={`${s.icon} mr-1`}></i>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {filteredMovies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/article/${movie.id}`}
                  className="group cursor-pointer"
                  data-product-shop
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      title={`秋霞影院 ${movie.title} ${categoryName}在线观看`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-transparent to-transparent"></div>
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-background-50/80 text-foreground-200 text-xs font-medium rounded-md whitespace-nowrap">
                        {movie.quality}
                      </span>
                    </div>
                    {movie.isNew && (
                      <div className="absolute top-2 right-2">
                        <span className="px-1.5 py-0.5 bg-primary-500 text-background-50 text-[10px] font-medium rounded whitespace-nowrap">
                          NEW
                        </span>
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2">
                      <span className="text-xs text-foreground-300 bg-background-50/80 px-1.5 py-0.5 rounded">
                        ★ {movie.rating}
                      </span>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/90">
                        <i className="ri-play-fill text-background-50 text-xl ml-0.5"></i>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-100 group-hover:text-accent-400 transition-colors truncate">
                    {movie.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                    <span>{movie.year}</span>
                    <span>·</span>
                    <span>{movie.subtype}</span>
                  </div>
                </Link>
              ))}
            </div>

            {filteredMovies.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-movie-2-line text-foreground-600 text-3xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}